/**
 * The pages in the console's nav that do not have their feature yet.
 *
 * `ux.md` §12 bans the dead end — a control that silently does nothing — and this is the honest
 * alternative for an AREA: the nav entry stays, because the map of the product is part of the
 * product, and the page says in one line what it will hold and what to do in the meantime.
 *
 * Every panel names a DESTINATION, not a date. "Arrives in a later release" with no forwarding
 * address is the same dead end wearing a apology; "the branding editor is in Settings today, and
 * moves here" is something a reader can act on.
 */
import { Callout, PageHeader } from '@flowza/ui'
import type { ReactNode } from 'react'

export type PlaceholderProps = {
  readonly title: string
  readonly description: string
  /** Where the capability lives today, or what it is waiting on. One sentence. */
  readonly meanwhile: ReactNode
  readonly tone?: 'info' | 'warning'
}

export function Placeholder({ title, description, meanwhile, tone = 'info' }: PlaceholderProps) {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={title} description={description} />
      <Callout tone={tone} title="Not on this screen yet">
        <p className="m-0 text-sm">{meanwhile}</p>
      </Callout>
    </div>
  )
}

export function PaymentsPlaceholder() {
  return (
    <Placeholder
      title="Payment Services"
      description="Collect a payment as part of signing."
      meanwhile="Coming soon. There is no payment provider connected and no payment field a sender can place, so nothing on this screen would do anything yet — which is why the screen does not pretend to have switches."
      tone="warning"
    />
  )
}

// `DocumentsPlaceholder` went the same way (ADR 0106): `admin/Documents.tsx` is the real page.
// `CompliancePlaceholder`, `StoragePlaceholder`, `SsoPlaceholder` and `SubscriptionPlaceholder`
// used to live here. Each was replaced by a real page (routes/index.tsx says which) and each kept
// exporting a sentence that had become false — "no connector is available yet" beside four
// connectors. Dead code that lies is worse than dead code, so they are gone rather than kept.
//
// `BrandingPlaceholder` went for the opposite reason (ADR 0134): its sentence stayed TRUE — branding
// really is organization-wide and really does live in Settings — which is what made the screen
// pointless. A page whose whole content is a forwarding address is a redirect written the long way,
// so `/app/admin/workspaces/:id/branding` is one now.
